import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CircularProgress, Stack, Typography } from '@mui/material';
import Button from '/@/components/button';
import { _ } from '/@/locale';
import useIsMobile from '/@/utils/hooks/is-mobile';
import type { BlockImageKey } from '/@/views/recommendations/v2/block-image';
import { BLOCK_LABELS } from '/@/views/recommendations/v2/result/use-copy-block-image';

/**
 * A titled section with an optional "copy as image" button.
 *
 * Shared by the v2 result page and the wizard's management step, so both render the same
 * blocks (density, phenology, nutrients) with the same copy affordance.
 */
function Section({
    title,
    subtitle,
    children,
    block,
    onCopy,
    copying,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    /** Identifies the block for the copy button's test id; renders the button when set. */
    block?: BlockImageKey;
    onCopy?: () => void;
    copying?: boolean;
}) {
    const isMobile = useIsMobile();
    return (
        <Stack spacing={2}>
            <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                <Stack width='100%'>
                    <Stack direction='row' alignItems='center' spacing={1} justifyContent='space-between'>
                        <Typography variant='h6' fontWeight='bold' color='primary'>
                            {title}
                        </Typography>
                        {block && onCopy && (
                            <Button
                                startIcon={
                                    copying ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <ContentCopyIcon fontSize='small' sx={{ color: 'primary.onSurface' }} />
                                    )
                                }
                                onClick={onCopy}
                                disabled={copying}
                                size='small'
                                data-testid={`copy-block-image-${block}`}
                            >
                                {isMobile
                                    ? null
                                    : _('Copiar {{block}}', {
                                          block: BLOCK_LABELS[block]().toLowerCase(),
                                      })}
                            </Button>
                        )}
                    </Stack>
                    {subtitle && (
                        <Typography variant='body2' color='primary'>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            {children}
        </Stack>
    );
}

export default Section;
